import * as Popover from '@radix-ui/react-popover';
import { CalendarRange } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { METRICS_MAX_WINDOW_HOURS, METRICS_MIN_WINDOW_HOURS } from '@pf/contracts';
import type { MetricsRange } from '@/lib/api/ops-queries';
import {
  epochMsToLocalInput,
  hours,
  localInputToEpochMs,
  nowMs,
  toEpochMs,
  toIso,
} from '@/lib/clock';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/primitives';

/**
 * "Which window am I looking at?" — the quick-select pill styling, and the custom range behind it.
 *
 * This started life private to `dashboard-page.tsx`, which was the right size for it while exactly
 * one screen took a {@link MetricsRange}. It is here now because a second one does, and the two
 * facts that made it worth moving are both about *agreement between screens* rather than about
 * saving lines:
 *
 *  - **The same window has to mean the same thing everywhere.** An operator who narrows the
 *    dashboard to the four hours around an incident and then opens the abandonment leaderboard is
 *    asking one question in two shapes ("is abandonment up?" then "who is doing it?"). The API
 *    already guarantees the two answer over identical bounds — `apps/api`'s `resolveRange` is
 *    shared by all three range-taking endpoints for precisely that reason — and a console with two
 *    pickers would have re-introduced the drift on the client side, where the bounds are actually
 *    chosen.
 *  - **The validation is the contract, restated.** Every rule in {@link checkRangeSpan} is one the
 *    API enforces too. Two copies of it would disagree eventually, and the symptom would be a
 *    range one screen accepts and the other 400s on — during an incident, which is the only time
 *    anybody types an absolute range at all.
 *
 * What is deliberately *not* here: the sentence a page writes about its own window. The dashboard
 * says "Business KPIs over the last 24 hours" and the leaderboard says "…over the last 24 hours.
 * Worst first."; those are page prose, they take the window as a variable, and pushing them in
 * here would buy nothing but a component with an opinion about grammar it cannot see. Each page
 * keeps its own duration formatter for that. The one this module owns
 * ({@link boundText}) exists only to name the contract's own bounds inside the picker's own
 * messages, and never leaves it.
 *
 * The strings are looked up here rather than taken as props, following `Pager` and `SortControl`:
 * a shared control's *chrome* ("From", "Apply", "the range is too long") belongs to the control,
 * while text about the page's subject belongs to the page. Nothing in this popover is about the
 * subject — it is the same popover on every screen that has a window.
 */

/**
 * The pill every window/measure toggle in a filter group wears; only the pressed state differs.
 *
 * Exported rather than duplicated per page because the picker's trigger is a *member* of those
 * groups: it sits in the same `role="group"` as the 1d/1w/1m buttons and has to be visually
 * indistinguishable from them, which a component cannot promise while each page styles its own
 * siblings. The dashboard's count/value toggle uses it too — same affordance, same group, same
 * pill.
 */
export function windowButtonClasses(pressed: boolean): string {
  return cn(
    'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
    pressed
      ? 'border-primary bg-accent text-accent-foreground'
      : 'border-border bg-card text-muted-foreground hover:text-foreground',
  );
}

/**
 * "1 hour" / "30 days" — a *bound* of the contract, spoken, for the picker's own messages.
 *
 * Deliberately much simpler than either page's window formatter, because it is only ever handed
 * {@link METRICS_MIN_WINDOW_HOURS} and {@link METRICS_MAX_WINDOW_HOURS}: both are whole hours by
 * construction, so the fractional and sub-hour cases a *measured* window can produce cannot arise
 * here. The days form is reserved for exact multiples for the same reason it is on the dashboard —
 * "1.3 days" is harder to picture than "31.2 hours".
 */
function boundText(t: TFunction, spanHours: number): string {
  if (spanHours > 24 && spanHours % 24 === 0) {
    return t('layout.rangePicker.days', { count: spanHours / 24 });
  }
  return t('layout.rangePicker.hours', { count: Math.round(spanHours * 10) / 10 });
}

/** The verdict on a from/to pair: the instants it names, or why it was refused. */
export type RangeCheck =
  | { readonly ok: true; readonly fromMs: number; readonly toMs: number }
  | { readonly ok: false; readonly messageKey: string; readonly boundHours: number };

/**
 * Decide whether a pair of instants may be sent.
 *
 * Every rule here is one the API enforces too, and that is the point: the console checks first so
 * a typo comes back as a sentence next to the field that caused it, in a tenth of the time, and
 * without spending a request on an answer that was never going to arrive. The bounds come from
 * `@pf/contracts` rather than from literals so the two cannot drift apart.
 *
 * The order matters. "The end cannot be in the future" is checked before the span bounds so an
 * operator who typed next month hears *that* rather than "at most 30 days".
 *
 * Split out from {@link validateRange} so the same rules can judge a range that arrived as two
 * already-resolved instants rather than as two `datetime-local` values — a range restored from the
 * address bar (see {@link readCustomRange}). One rule set, two entry points; a URL that a page
 * would accept is exactly a URL the picker would have produced.
 *
 * @param fromMs - the range's start, in epoch milliseconds.
 * @param toMs - the range's end, in epoch milliseconds.
 * @param at - the instant "the future" is measured against.
 */
export function checkRangeSpan(fromMs: number, toMs: number, at: number): RangeCheck {
  if (Number.isNaN(fromMs) || Number.isNaN(toMs)) {
    return { ok: false, messageKey: 'layout.rangePicker.errors.incomplete', boundHours: 0 };
  }
  if (toMs <= fromMs) {
    return { ok: false, messageKey: 'layout.rangePicker.errors.order', boundHours: 0 };
  }
  if (toMs > at) {
    return { ok: false, messageKey: 'layout.rangePicker.errors.future', boundHours: 0 };
  }
  const span = (toMs - fromMs) / 3_600_000;
  if (span < METRICS_MIN_WINDOW_HOURS) {
    return {
      ok: false,
      messageKey: 'layout.rangePicker.errors.tooShort',
      boundHours: METRICS_MIN_WINDOW_HOURS,
    };
  }
  if (span > METRICS_MAX_WINDOW_HOURS) {
    return {
      ok: false,
      messageKey: 'layout.rangePicker.errors.tooLong',
      boundHours: METRICS_MAX_WINDOW_HOURS,
    };
  }
  return { ok: true, fromMs, toMs };
}

/**
 * Decide whether a from/to pair *as typed* may be sent.
 *
 * An incomplete pair is checked before a reversed one — which falls out of resolving the fields
 * first — because a half-typed date is not a mistake yet, and complaining about the order of two
 * dates when one of them is still `2026-08-__` would be answering a question nobody asked.
 *
 * @param fromValue - the `from` field's raw `datetime-local` value.
 * @param toValue - the `to` field's raw `datetime-local` value.
 * @param at - the instant "the future" is measured against.
 */
export function validateRange(fromValue: string, toValue: string, at: number): RangeCheck {
  return checkRangeSpan(localInputToEpochMs(fromValue), localInputToEpochMs(toValue), at);
}

/**
 * Rebuild a custom range from two strings that came out of a URL, or refuse it.
 *
 * A screen that keeps its range in the address bar (the abandonment leaderboard does; the
 * dashboard keeps it in component state) inherits the problem every URL-held filter has: the pair
 * can be truncated, hand-edited, pasted from an older build, or simply stale — a link shared last
 * month whose `to` is now inside the 720 h cap but whose *span* never was. None of that may throw
 * inside a render, and none of it may reach the API as a request that is going to 400.
 *
 * So the pair is held to exactly the rules the picker applies — same {@link checkRangeSpan}, same
 * bounds — and anything that fails simply is not a custom range, which lets the caller fall back to
 * its preset. That is `lib/nav/list-state.ts`'s "reads are total" rule applied to a value too
 * structured for a `ParamReader` to judge on its own: a reader can say "that is an ISO instant",
 * but only this can say "those two instants are a window this console will ask for".
 *
 * @param from - the `from` param, ISO-8601 with offset, or `''` when absent.
 * @param to - the `to` param, ISO-8601 with offset, or `''` when absent.
 * @param at - the instant "the future" is measured against.
 * @returns the range, or `null` when the pair is absent, incomplete or out of bounds.
 */
export function readCustomRange(from: string, to: string, at: number): MetricsRange | null {
  if (from === '' || to === '') return null;
  const checked = checkRangeSpan(toEpochMs(from), toEpochMs(to), at);
  return checked.ok ? { kind: 'custom', from, to } : null;
}

/** Props for {@link CustomRangePicker}. */
export interface CustomRangePickerProps {
  /** The selection currently driving the screen, used to seed and to light the trigger. */
  range: MetricsRange;
  /** Called with a validated absolute range when the operator applies one. */
  onApply: (range: MetricsRange) => void;
  /** Called when the operator clears a custom range. What that falls back to is the page's call. */
  onClear: () => void;
}

/**
 * The "Custom" member of a window group: a popover holding an absolute from/to pair.
 *
 * A popover rather than a modal because choosing a window is a *filter*, not a decision — the
 * screen behind it stays readable, which is exactly what an operator comparing "last week" to
 * "the four hours around the incident" wants. Radix owns the parts that are easy to get wrong and
 * tedious to re-prove: focus moves into the panel on open and back to the trigger on close, Escape
 * and an outside click dismiss it, and the trigger carries `aria-expanded`/`aria-controls`. What
 * this component adds is the `aria-pressed` the sibling presets already use, so a screen reader
 * hears one consistent group rather than N toggles and a menu.
 *
 * Native `<input type="datetime-local">` rather than a calendar widget: it is keyboard-navigable,
 * localised, and validated by the browser for free, and every alternative is a dependency.
 *
 * **The zone conversion is the load-bearing part.** A `datetime-local` value is local wall clock
 * with no offset — `2026-08-05T08:00` says nothing about *where* 08:00 is — while the API takes
 * ISO-8601 *with* offset. {@link localInputToEpochMs} resolves the value in the browser's zone and
 * {@link toIso} renders that instant as UTC, which is a valid offset form and spares us
 * hand-assembling `+07:00` (and getting it wrong on the DST boundary the operator is most likely
 * to be investigating). The reverse trip — response back into the fields, and into the header —
 * goes through {@link epochMsToLocalInput} and `formatDateTimeCompact`, both local.
 */
export function CustomRangePicker({ range, onApply, onClear }: CustomRangePickerProps): ReactNode {
  const { t } = useTranslation();
  const fromId = useId();
  const toId = useId();
  const errorId = useId();
  const [open, setOpen] = useState(false);
  const [fromValue, setFromValue] = useState('');
  const [toValue, setToValue] = useState('');
  const [error, setError] = useState<RangeCheck & { ok: false }>();

  const active = range.kind === 'custom';

  /**
   * Seed the fields from whatever is on screen, so the picker opens on a *valid* range the
   * operator can nudge rather than on two empty boxes. Re-seeding on every open (rather than
   * keeping the last edit) is deliberate: an abandoned half-typed range is not worth restoring.
   */
  function handleOpenChange(next: boolean): void {
    setOpen(next);
    setError(undefined);
    if (!next) return;
    const endMs = range.kind === 'custom' ? toEpochMs(range.to) : nowMs();
    const startMs = range.kind === 'custom' ? toEpochMs(range.from) : endMs - hours(range.hours);
    setFromValue(epochMsToLocalInput(startMs));
    setToValue(epochMsToLocalInput(endMs));
  }

  function handleApply(): void {
    const checked = validateRange(fromValue, toValue, nowMs());
    if (!checked.ok) {
      setError(checked);
      return;
    }
    onApply({ kind: 'custom', from: toIso(checked.fromMs), to: toIso(checked.toMs) });
    setOpen(false);
  }

  /** Clear the error as soon as the operator starts fixing it — a stale complaint reads as a bug. */
  function edit(set: (value: string) => void, value: string): void {
    set(value);
    setError(undefined);
  }

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <button type="button" aria-pressed={active} className={windowButtonClasses(active)}>
          <CalendarRange className="size-3" aria-hidden="true" />
          {t('layout.rangePicker.custom')}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          aria-label={t('layout.rangePicker.title')}
          className="z-50 w-[min(20rem,calc(100vw-2rem))] rounded-lg border border-border bg-card p-4 text-left shadow-lg focus:outline-none"
        >
          <p className="text-sm font-semibold text-foreground">{t('layout.rangePicker.title')}</p>
          <p className="mt-1 text-2xs leading-relaxed text-muted-foreground">
            {t('layout.rangePicker.description', {
              max: boundText(t, METRICS_MAX_WINDOW_HOURS),
            })}
          </p>

          <div className="mt-3 space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor={fromId}>{t('layout.rangePicker.from')}</Label>
              <Input
                id={fromId}
                type="datetime-local"
                value={fromValue}
                aria-invalid={error !== undefined}
                aria-describedby={error === undefined ? undefined : errorId}
                onChange={(event) => {
                  edit(setFromValue, event.target.value);
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={toId}>{t('layout.rangePicker.to')}</Label>
              <Input
                id={toId}
                type="datetime-local"
                value={toValue}
                aria-invalid={error !== undefined}
                aria-describedby={error === undefined ? undefined : errorId}
                onChange={(event) => {
                  edit(setToValue, event.target.value);
                }}
              />
            </div>
          </div>

          {error === undefined ? null : (
            <p
              id={errorId}
              role="alert"
              className="mt-3 text-2xs font-medium leading-relaxed text-destructive"
            >
              {t(error.messageKey, { duration: boundText(t, error.boundHours) })}
            </p>
          )}

          <div className="mt-4 flex justify-end gap-2">
            {/* Only offered once a custom range is actually in force — there is nothing to clear
                otherwise, and a permanently dead button is worse than no button. */}
            {active ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  onClear();
                  setOpen(false);
                }}
              >
                {t('layout.rangePicker.clear')}
              </Button>
            ) : null}
            <Popover.Close asChild>
              <Button variant="ghost" size="sm">
                {t('layout.rangePicker.cancel')}
              </Button>
            </Popover.Close>
            <Button size="sm" onClick={handleApply}>
              {t('layout.rangePicker.apply')}
            </Button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
